import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKDurationToRESTDuration } from '@wix/sdk-runtime/transformations/duration';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsScheduleScheduleManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/wix-events-agenda',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    _: [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/wix-events-schedule/v1/schedule',
        destPath: '/v1/schedule',
      },
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'api._base_domain_': [
      {
        srcPath: '/events/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/wix-events-web/v1/schedule',
        destPath: '/v1/schedule',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_schedule';

/**
 * Retrieves a list of up to 100 schedule items, with basic filter support.
 *
 * Default filters to schedule items with a `state` of `PUBLISHED` and `VISIBLE`.
 */
export function listScheduleItems(payload: object): RequestOptionsFactory<any> {
  function __listScheduleItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'startingFrom' }, { path: 'startingBefore' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'GET' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.ListScheduleItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'items.createdDate' },
              { path: 'items.updatedDate' },
              { path: 'items.timeSlot.start' },
              { path: 'items.timeSlot.end' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listScheduleItems;
}

/**
 * Creates a query to retrieve a list of schedule items.
 *
 * The `queryScheduleItems( )` method builds a query to retrieve a list of schedule items and returns a `ItemsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/events/schedule/items-query-builder/find) method.
 *
 * You can refine the query by chaining `ItemsQueryBuilder` methods onto the query. `ItemsQueryBuilder` methods enable you to sort, filter, and control the results `queryScheduleItems( )` returns.
 *
 * `queryScheduleItems( )` runs with these `ItemsQueryBuilder` defaults, which you can override:
 *
 *  - [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/events/schedule/items-query-builder/limit)
 *  - [`descending("_createdDate")`](https://dev.wix.com/docs/sdk/backend-modules/events/schedule/items-query-builder/descending)
 *
 * The methods that are chained to `queryScheduleItems( )` are applied in the order they're called. For example, if you apply `ascending('name')` and then `descending('stageName')`, the results are sorted first by the `name`, and then, if there are multiple results with the same `name`, the items are sorted by `stageName`.
 */
export function queryScheduleItems(
  payload: object
): RequestOptionsFactory<any> {
  function __queryScheduleItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'POST' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.QueryScheduleItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'items.createdDate' },
              { path: 'items.updatedDate' },
              { path: 'items.timeSlot.start' },
              { path: 'items.timeSlot.end' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryScheduleItems;
}

/** Retrieves a schedule item. */
export function getScheduleItem(payload: object): RequestOptionsFactory<any> {
  function __getScheduleItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'GET' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.GetScheduleItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/{itemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.createdDate' },
              { path: 'item.updatedDate' },
              { path: 'item.timeSlot.start' },
              { path: 'item.timeSlot.end' },
              { path: 'draft.createdDate' },
              { path: 'draft.updatedDate' },
              { path: 'draft.timeSlot.start' },
              { path: 'draft.timeSlot.end' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getScheduleItem;
}

/**
 * Adds a schedule item to a draft schedule.  For the same date and time you can have multiple items.
 *
 * Note that draft items are not publicly available unless published.
 */
export function addScheduleItem(payload: object): RequestOptionsFactory<any> {
  function __addScheduleItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'item.timeSlot.start' }, { path: 'item.timeSlot.end' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'POST' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.AddScheduleItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/draft',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.createdDate' },
              { path: 'item.updatedDate' },
              { path: 'item.timeSlot.start' },
              { path: 'item.timeSlot.end' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addScheduleItem;
}

/**
 * Updates an existing schedule item.
 *
 * The changes are performed on a draft schedule item, even if the schedule item has already been published.
 */
export function updateScheduleItem(
  payload: object
): RequestOptionsFactory<any> {
  function __updateScheduleItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fields' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'item.timeSlot.start' }, { path: 'item.timeSlot.end' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'PATCH' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.UpdateScheduleItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/draft/{itemId}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.createdDate' },
              { path: 'item.updatedDate' },
              { path: 'item.timeSlot.start' },
              { path: 'item.timeSlot.end' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateScheduleItem;
}

/** Deletes a schedule item from a draft schedule. */
export function deleteScheduleItem(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteScheduleItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'DELETE' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.DeleteScheduleItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/draft/items',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteScheduleItem;
}

/** Clears all changes to a draft schedule. */
export function discardDraft(payload: object): RequestOptionsFactory<any> {
  function __discardDraft({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'DELETE' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.DiscardDraft',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/draft',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __discardDraft;
}

/** Publishes a draft schedule. */
export function publishDraft(payload: object): RequestOptionsFactory<any> {
  function __publishDraft({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'POST' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.PublishDraft',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/publish',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __publishDraft;
}

/** Adjusts the time of all draft schedule items for an event at once. */
export function rescheduleDraft(payload: object): RequestOptionsFactory<any> {
  function __rescheduleDraft({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKDurationToRESTDuration,
        paths: [{ path: 'timeSlotOffset' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.schedule.v1.schedule_item',
      method: 'POST' as any,
      methodFqn: 'wix.events.schedule.ScheduleManagement.RescheduleDraft',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsScheduleScheduleManagementUrl({
        protoPath: '/v1/schedule/draft/reschedule',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __rescheduleDraft;
}
